import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api';

interface Room {
  roomKey: string;
  examType: 'WAEC' | 'JAMB';
  subjectName: string;
}

const rules = [
  'Keep discussion relevant to the subject and treat learners respectfully.',
  'Do not share personal information, abusive content, spam, or live exam answers.',
  'Use moderator controls responsibly. Removed participants cannot rejoin this room.',
  'Room messages are visible to participants and may be deleted during moderation.',
];

const coordinatorSessionKey = (roomKey: string) => `discussion-room-coordinator:${roomKey}`;

const DiscussionCoordinatorLogin: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomKey, setRoomKey] = useState(searchParams.get('roomKey') || '');
  const [displayName, setDisplayName] = useState('');
  const [coordinatorId, setCoordinatorId] = useState('');
  const [acceptedRules, setAcceptedRules] = useState(false);
  const [loadingRooms, setLoadingRooms] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    api.get('/discussion-rooms')
      .then((response) => {
        if (mounted) setRooms(Array.isArray(response.data?.data) ? response.data.data : []);
      })
      .catch((requestError: any) => {
        if (mounted) setError(requestError?.response?.data?.message || 'Unable to load the room list.');
      })
      .finally(() => {
        if (mounted) setLoadingRooms(false);
      });
    return () => { mounted = false; };
  }, []);

  const selectedRoom = useMemo(() => rooms.find((room) => room.roomKey === roomKey), [rooms, roomKey]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!selectedRoom || !displayName.trim() || !coordinatorId.trim() || !acceptedRules || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const response = await api.post(
        `/discussion-rooms/${encodeURIComponent(roomKey)}/enter`,
        { acknowledgedRules: true, coordinatorId: coordinatorId.trim(), displayName: displayName.trim() },
        { headers: { 'x-discussion-guest-login': 'true' } }
      );
      const coordinatorToken = response.data?.data?.coordinatorToken;
      if (!coordinatorToken) throw new Error('Coordinator access could not be established. Contact the administrator.');
      sessionStorage.setItem(coordinatorSessionKey(roomKey), coordinatorToken);
      navigate(`/discussion-rooms?room=${encodeURIComponent(roomKey)}`, { replace: true });
    } catch (requestError: any) {
      setError(requestError?.response?.data?.message || requestError?.message || 'Coordinator sign-in failed. Check your room ID.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 6 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/discussion-rooms')} sx={{ mb: 2 }}>
        All discussion rooms
      </Button>
      <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 2 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <ForumOutlinedIcon color="primary" />
          <Typography variant="overline" color="primary.main" sx={{ fontWeight: 800, letterSpacing: '0.1em' }}>
            ROOM COORDINATOR ACCESS
          </Typography>
        </Stack>
        <Typography component="h1" variant="h4" sx={{ fontWeight: 800, mb: 1 }}>Coordinator sign in</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Use the room ID supplied by the administrator. This signs you in only to the selected room and does not require a student account.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {loadingRooms ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
        ) : (
          <Box component="form" onSubmit={(event) => void handleSubmit(event)}>
            <Stack spacing={2.25}>
              <FormControl fullWidth required>
                <InputLabel id="coordinator-room-label">Subject room</InputLabel>
                <Select
                  labelId="coordinator-room-label"
                  label="Subject room"
                  value={roomKey}
                  onChange={(event) => setRoomKey(event.target.value)}
                >
                  {rooms.map((room) => (
                    <MenuItem key={room.roomKey} value={room.roomKey}>{room.examType} · {room.subjectName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedRoom && (
                <Alert severity="info" icon={false}>
                  Selected room: <strong>{selectedRoom.examType} · {selectedRoom.subjectName}</strong>
                </Alert>
              )}
              <TextField
                label="Display name"
                value={displayName}
                onChange={(event) => setDisplayName(event.target.value)}
                autoComplete="name"
                inputProps={{ maxLength: 80 }}
                required
                fullWidth
              />
              <TextField
                label="Coordinator ID"
                value={coordinatorId}
                onChange={(event) => setCoordinatorId(event.target.value)}
                autoComplete="off"
                required
                fullWidth
              />
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Room rules</Typography>
                <Stack component="ol" spacing={0.75} sx={{ pl: 2.5, m: 0 }}>
                  {rules.map((rule) => <Typography component="li" variant="body2" color="text.secondary" key={rule}>{rule}</Typography>)}
                </Stack>
              </Box>
              <FormControlLabel
                control={<Checkbox checked={acceptedRules} onChange={(event) => setAcceptedRules(event.target.checked)} />}
                label="I have read and agree to enforce these room rules."
              />
              <Button type="submit" variant="contained" size="large" disabled={submitting || !acceptedRules || !roomKey || !displayName.trim() || !coordinatorId.trim()}>
                {submitting ? 'Verifying ID…' : 'Sign in to room'}
              </Button>
            </Stack>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default DiscussionCoordinatorLogin;
